import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Plus, Hand, Wallet, Trash2, ShieldAlert } from 'lucide-react'
import { accountService, debtService } from '../lib/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { formatRupees } from '../utils/currency'

const DebtsPage = () => {
  const [loading, setLoading] = useState(true)
  const [debts, setDebts] = useState([])
  const [stats, setStats] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    lender_name: '',
    original_principal: '',
    remaining_principal: '',
    interest_rate: '',
    emi_amount: '',
    due_day: '1',
    account_id: '',
    notes: '',
  })

  const accountMap = useMemo(() => {
    return accounts.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
  }, [accounts])

  const loadData = async () => {
    try {
      setLoading(true)
      const [debtData, debtStats, accountData] = await Promise.all([
        debtService.getDebts(),
        debtService.getStats(),
        accountService.getAccounts(),
      ])

      setDebts(debtData.debts || [])
      setStats(debtStats || null)
      setAccounts(accountData.accounts || [])
    } catch (error) {
      toast.error(error.message || 'Failed to load debts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleCreateDebt = async (e) => {
    e.preventDefault()

    try {
      await debtService.createDebt({
        ...formData,
        original_principal: Number(formData.original_principal),
        remaining_principal: Number(formData.remaining_principal || formData.original_principal),
        interest_rate: Number(formData.interest_rate || 0),
        emi_amount: Number(formData.emi_amount || 0),
        due_day: Number(formData.due_day || 1),
        account_id: formData.account_id || null,
      })

      toast.success('Debt added successfully')
      setShowCreateModal(false)
      setFormData({
        name: '',
        lender_name: '',
        original_principal: '',
        remaining_principal: '',
        interest_rate: '',
        emi_amount: '',
        due_day: '1',
        account_id: '',
        notes: '',
      })
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to create debt')
    }
  }

  const handlePayDebt = async (debt) => {
    const amountInput = window.prompt(`Payment amount for ${debt.name}`, debt.emi_amount || '1000')
    if (!amountInput) return

    try {
      await debtService.payDebt(debt.id, { amount: Number(amountInput) })
      toast.success('Debt payment recorded')
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to record debt payment')
    }
  }

  const handleCloseDebt = async (debtId) => {
    if (!window.confirm('Mark this debt as closed?')) return

    try {
      await debtService.deleteDebt(debtId)
      toast.success('Debt marked as closed')
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to close debt')
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading debts..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Debts & EMI Tracker</h1>
          <p className="text-gray-600 dark:text-gray-400">Monitor liabilities, payments, and payoff momentum.</p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Debt
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Active Debts</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.active_debts || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Outstanding</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatRupees(stats?.total_remaining || 0)}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Paid</p>
          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{formatRupees(stats?.total_paid || 0)}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Monthly EMI</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatRupees(stats?.monthly_emi_total || 0)}</p>
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Debt Accounts</h2>
        {debts.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No debt records yet.</p>
        ) : (
          <div className="space-y-3">
            {debts.map((debt) => {
              const original = Number(debt.original_principal ?? debt.principal_amount ?? 0)
              const remaining = Number(debt.remaining_principal ?? debt.remaining_balance ?? 0)
              const progress = original > 0 ? ((original - remaining) / original) * 100 : 0
              const isClosed = debt.status === 'closed'

              return (
                <div key={debt.id} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 dark:text-white">{debt.name}</p>
                        {isClosed ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">Closed</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">Active</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Lender: {debt.lender_name || debt.lender || 'N/A'} • EMI: {formatRupees(debt.emi_amount || 0)} • Due day: {debt.due_day || '-'}
                      </p>

                      <div className="mt-2 h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
                        <div className="h-full bg-primary-500" style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }} />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Paid: {formatRupees(original - remaining)} / {formatRupees(original)} ({progress.toFixed(1)}%)
                      </p>
                      {debt.account_id && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Linked account: {accountMap[debt.account_id]?.name || debt.account_id}
                        </p>
                      )}
                    </div>

                    <div className="text-right flex flex-col items-end gap-2">
                      <p className="font-bold text-gray-900 dark:text-white">{formatRupees(remaining)}</p>
                      {!isClosed && (
                        <button
                          onClick={() => handlePayDebt(debt)}
                          className="text-sm px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
                        >
                          <Hand className="h-3.5 w-3.5" /> Pay
                        </button>
                      )}
                      {!isClosed && (
                        <button
                          onClick={() => handleCloseDebt(debt.id)}
                          className="text-sm px-3 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-200 inline-flex items-center gap-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Close
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Create Debt</h3>
            <form onSubmit={handleCreateDebt} className="space-y-4">
              <input
                required
                className="input-field"
                placeholder="Debt name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              />

              <input
                className="input-field"
                placeholder="Lender name"
                value={formData.lender_name}
                onChange={(e) => setFormData((prev) => ({ ...prev, lender_name: e.target.value }))}
              />

              <input
                required
                type="number"
                min="0"
                step="0.01"
                className="input-field"
                placeholder="Original principal"
                value={formData.original_principal}
                onChange={(e) => setFormData((prev) => ({ ...prev, original_principal: e.target.value }))}
              />

              <input
                type="number"
                min="0"
                step="0.01"
                className="input-field"
                placeholder="Remaining principal (optional)"
                value={formData.remaining_principal}
                onChange={(e) => setFormData((prev) => ({ ...prev, remaining_principal: e.target.value }))}
              />

              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input-field"
                  placeholder="Interest %"
                  value={formData.interest_rate}
                  onChange={(e) => setFormData((prev) => ({ ...prev, interest_rate: e.target.value }))}
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input-field"
                  placeholder="EMI amount"
                  value={formData.emi_amount}
                  onChange={(e) => setFormData((prev) => ({ ...prev, emi_amount: e.target.value }))}
                />
              </div>

              <input
                type="number"
                min="1"
                max="31"
                className="input-field"
                placeholder="Due day (1-31)"
                value={formData.due_day}
                onChange={(e) => setFormData((prev) => ({ ...prev, due_day: e.target.value }))}
              />

              <select
                className="input-field"
                value={formData.account_id}
                onChange={(e) => setFormData((prev) => ({ ...prev, account_id: e.target.value }))}
              >
                <option value="">No linked account</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({formatRupees(account.balance)})
                  </option>
                ))}
              </select>

              <textarea
                className="input-field"
                rows={2}
                placeholder="Notes"
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              />

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1 inline-flex items-center justify-center gap-2">
                  <Wallet className="h-4 w-4" />
                  Save
                </button>
              </div>
            </form>

            <p className="text-xs text-amber-600 dark:text-amber-300 mt-4 inline-flex items-center gap-1">
              <ShieldAlert className="h-3.5 w-3.5" />
              Tip: Keep remaining principal updated for accurate payoff projections.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default DebtsPage
